import type {
  AmendmentDiagnosticData,
  AmendmentDiagnosticPatch,
} from "../hooks/useAmendmentDiagnosticDraft";
import { api } from "../lib";
import type { DocumentViewResponse } from "../types/reportTemplate";
import {
  ReportContentChangedError,
  type IssuedReportReview,
  type ReportContentVersion,
  type ReportPreviewData,
  type ReportPreviewEvidence,
} from "./signOff.service";
import type { AddendumMacro } from "./addendum.service";

export interface AmendmentDraft {
  id: string;
  caseId: string;
  reason: string;
  contentHtml: string;
  sourceMacroId: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
  diagnosticData?: AmendmentDiagnosticData | null;
  baseSignOffId?: string | null;
  baseReportRevision?: number | null;
}

export interface AmendmentDraftInput {
  reason?: string;
  contentHtml?: string;
  sourceMacroId?: string | null;
  revision?: number;
  diagnosticContext?: boolean;
  diagnosticPatch?: AmendmentDiagnosticPatch;
}

export interface AmendmentOptions {
  macros: AddendumMacro[];
}

export interface AmendmentSignOutResult {
  success: true;
  signOffId: string;
  reportVersion: number;
  deliveryQueue: unknown[];
  deliveryQueueFailed: boolean;
  pdfGenerationQueueFailed: boolean;
  sessionId: string;
  sessionExpiresAt?: string;
}

export interface AmendmentSignOutPreview {
  previewData: ReportPreviewData;
  documentViewData: DocumentViewResponse;
  review: IssuedReportReview;
  templateId: string | null;
  draftRevision: number;
  baseReportVersion: number;
  pathologistComments?: any[];
  linkedStainRequests?: Array<{
    specimenId: string;
    stainId: string;
    stainName: string;
  }>;
}

export type AmendmentServiceErrorKind =
  | "authentication_required"
  | "review_required"
  | "review_stale"
  | "forbidden"
  | "validation"
  | "conflict"
  | "network"
  | "failed";

export class AmendmentServiceError extends Error {
  readonly kind: AmendmentServiceErrorKind;

  readonly sessionId?: string;

  readonly sessionExpiresAt?: string;

  constructor(
    kind: AmendmentServiceErrorKind,
    message: string,
    session?: { sessionId?: string; sessionExpiresAt?: string }
  ) {
    super(message);
    this.name = "AmendmentServiceError";
    this.kind = kind;
    this.sessionId = session?.sessionId;
    this.sessionExpiresAt = session?.sessionExpiresAt;
    Object.setPrototypeOf(this, AmendmentServiceError.prototype);
  }
}

interface AmendmentHttpError {
  response?: {
    status?: number;
    data?: {
      code?: string;
      reviewErrorCode?: string;
      requiresPassword?: boolean;
      sessionId?: string;
      sessionExpiresAt?: string;
    };
  };
}

function toAmendmentServiceError(error: unknown): AmendmentServiceError {
  const response = (error as AmendmentHttpError).response;
  const code = response?.data?.reviewErrorCode || response?.data?.code;
  const session = {
    sessionId: response?.data?.sessionId,
    sessionExpiresAt: response?.data?.sessionExpiresAt,
  };

  if (response?.data?.requiresPassword) {
    return new AmendmentServiceError(
      "authentication_required",
      "Enter your password to sign out this amendment.",
      session
    );
  }
  if (code === "REPORT_REVIEW_REQUIRED") {
    return new AmendmentServiceError(
      "review_required",
      "Review the current report before signing out this amendment.",
      session
    );
  }
  if (code === "REPORT_REVIEW_STALE") {
    return new AmendmentServiceError(
      "review_stale",
      "The report changed. Review the current version before signing out.",
      session
    );
  }
  if (response?.status === 403) {
    return new AmendmentServiceError(
      "forbidden",
      "You do not have permission to change this signed report."
    );
  }
  if (response?.status === 400 || response?.status === 422) {
    return new AmendmentServiceError(
      "validation",
      "Check the amendment details and try again."
    );
  }
  if (response?.status === 409) {
    return new AmendmentServiceError(
      "conflict",
      "The amendment changed in another session. Refresh the case and try again."
    );
  }
  if (response?.status === undefined) {
    return new AmendmentServiceError(
      "network",
      "The connection was interrupted. Check the amendment before trying again."
    );
  }
  return new AmendmentServiceError(
    "failed",
    "The amendment could not be completed. Refresh the case and try again."
  );
}

async function translateAmendmentError<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    throw toAmendmentServiceError(error);
  }
}

export const amendmentService = {
  getDraft: (caseId: string) =>
    translateAmendmentError(
      api.get<AmendmentDraft | null>(`/cases/${caseId}/amendment-draft`)
    ),
  getOptions: (caseId: string) =>
    translateAmendmentError(
      api.get<AmendmentOptions>(`/cases/${caseId}/amendment-options`)
    ),
  createDraft: (caseId: string, input: AmendmentDraftInput) =>
    translateAmendmentError(
      api.post<AmendmentDraft>(`/cases/${caseId}/amendment-drafts`, input)
    ),
  updateDraft: (caseId: string, draftId: string, input: AmendmentDraftInput) =>
    translateAmendmentError(
      api.patch<AmendmentDraft>(
        `/cases/${caseId}/amendment-drafts/${draftId}`,
        input
      )
    ),
  deleteDraft: (caseId: string, draftId: string) =>
    translateAmendmentError(
      api.delete<void>(`/cases/${caseId}/amendment-drafts/${draftId}`)
    ),
  getSignOutPreview: (caseId: string, draftId: string, revision: number) =>
    translateAmendmentError(
      api.get<AmendmentSignOutPreview>(
        `/cases/${caseId}/amendment-drafts/${draftId}/sign-off/preview?revision=${revision}`
      )
    ),
  acknowledgeSignOutPreview: async (
    caseId: string,
    draftId: string,
    revision: number,
    contentVersion: ReportContentVersion
  ): Promise<ReportPreviewEvidence> => {
    try {
      return await api.post<ReportPreviewEvidence>(
        `/cases/${caseId}/amendment-drafts/${draftId}/sign-off/preview-review`,
        { revision, contentVersion }
      );
    } catch (error) {
      const response = (error as AmendmentHttpError).response;
      if (
        response?.status === 409 &&
        response.data?.code === "REPORT_CONTENT_CHANGED"
      ) {
        throw new ReportContentChangedError();
      }
      throw toAmendmentServiceError(error);
    }
  },
  signOutDraft: (
    caseId: string,
    draftId: string,
    input: {
      revision: number;
      sessionId?: string | null;
      password?: string;
      previewEvidence?: ReportPreviewEvidence;
    }
  ) =>
    translateAmendmentError(
      api.post<AmendmentSignOutResult>(
        `/cases/${caseId}/amendment-drafts/${draftId}/sign-out`,
        input
      )
    ),
};
